/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PLANNED_JOBS_DETAIL_PAGE
 * Date:         2026-01-05
 * Modified By: Cursor
 *
 * Description:
 * Planned jobs detail page showing the list of 6 planned jobs from business requirements.
 * This is a documentation-only page showing future system jobs.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Adding: ~120 lines
 * - Projected: ~120 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/services/schedulerApi.ts
 * - server/controllers/admin/schedulerController.js
 */

import React, { useState, useEffect } from 'react';
import { Alert, Spinner, Card, Row, Col, ListGroup } from 'react-bootstrap';
import schedulerApi from '../../../services/schedulerApi';
import './AdminPlannedJobsPage.scss';

interface DashboardData {
  futureJobs: {
    status: string;
    totalPlanned: number;
    jobs: string[];
  };
}

const AdminPlannedJobsPage: React.FC = () => {
  // [2026-01-05] Planned jobs detail: State for dashboard data
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // [2026-01-05] Planned jobs detail: Load dashboard data on mount
  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await schedulerApi.fetchSchedulerDashboard();
      if (response.success && response.data) {
        setDashboard(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (status?: string) => {
    if (!status) return "Status: N/A";
    if (status === "documentation-only") return "Status: ⚫ Documentation Only";
    return `Status: ${status}`;
  };

  return (
    <div className="admin-planned-jobs-page">
      <h2>Future Jobs (Planned)</h2>
      <p className="text-muted mb-4">Planned system jobs – Not Yet Implemented</p>

      {/* [2026-01-05] Planned jobs detail: Display status and jobs list */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : dashboard ? (
            <>
              <Row className="mb-3">
                <Col>
                  <div className="mb-3">
                    <strong>{formatStatus(dashboard.futureJobs.status)}</strong>
                  </div>
                  <div className="mb-4">
                    <strong>Total Planned:</strong> {dashboard.futureJobs.totalPlanned} jobs
                  </div>
                  <div>
                    <h5 className="mb-3">Jobs Ready to Build:</h5>
                    <ListGroup>
                      {dashboard.futureJobs.jobs.map((job, index) => (
                        <ListGroup.Item key={index}>{job}</ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </Col>
              </Row>
            </>
          ) : (
            <Alert variant="warning">Unable to load planned jobs information.</Alert>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminPlannedJobsPage;
